import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import axios from "../../api/axios";

import {
  masterAttemptContainer,
  masterAttemptContainerActive,
  masterAttemptHeaderCard,
  masterAttemptSummaryCard,
  masterAttemptQuestionCard,
  masterAttemptSidebarCard,
  masterAttemptOptionCard,
  masterAttemptOptionSelected,
  masterAttemptOptionUnselected,
  masterAttemptModalOverlay,
  masterAttemptModalBox,
  masterAttemptNavBtnPrev,
  masterAttemptNavBtnClear,
  masterAttemptNavBtnReview,
  masterAttemptNavBtnSave,
  masterAttemptSubmitBtn,
  masterAttemptStartBtnActive,
  masterAttemptStartBtnDisabled,
  masterAttemptBackBtn,
} from "../../GlobalComponents/MasterCssClass";

const formatDecimal = (value) => Number(value || 0).toFixed(3);

const decodeStoredHtml = (value) => {
  if (typeof window === "undefined") return String(value || "");
  const input = String(value || "");
  const textArea = document.createElement("textarea");
  textArea.innerHTML = input;
  return textArea.value || input;
};

const toHtml = (value) => decodeStoredHtml(value).replace(/\n/g, "<br />");

const getQKey = (q, idx) => `q_idx_${idx}_${q?._id || "item"}`;

const PracticeSetAttempt = () => {
  const { subjectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [savedMap, setSavedMap] = useState({});
  const [markedMap, setMarkedMap] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [timeLeft, setTimeLeft] = useState(0);
  const [manualMinutes, setManualMinutes] = useState(30);
  const [isStarted, setIsStarted] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const [activeSet, setActiveSet] = useState(null);
  const [attemptSaved, setAttemptSaved] = useState(false);
  const mathRef = useRef(null);
  const containerRef = useRef(null);
  const selectedSetId = searchParams.get("questionSetId") || "";

  // Prevent Copy, Right-Click, and Keyboard Shortcuts
  useEffect(() => {
    const handleContextMenu = (e) => e.preventDefault();
    const handleCopy = (e) => {
      e.preventDefault();
      alert("Copying content is disabled during the practice set.");
    };
    const handleKeyDown = (e) => {
      if (
        e.keyCode === 123 ||
        (e.ctrlKey && e.shiftKey && (e.keyCode === 73 || e.keyCode === 74)) ||
        (e.ctrlKey && (e.keyCode === 85 || e.keyCode === 67 || e.keyCode === 86))
      ) {
        e.preventDefault();
      }
    };

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("copy", handleCopy);
    document.addEventListener("cut", handleCopy);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("copy", handleCopy);
      document.removeEventListener("cut", handleCopy);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Prevent Back Button
  useEffect(() => {
    if (isStarted && !submitted) {
      window.history.pushState(null, null, window.location.href);
      const handleBackButton = () => {
        window.history.pushState(null, null, window.location.href);
        alert("Back button is disabled! Use the 'Submit Test' button to finish.");
      };
      window.addEventListener("popstate", handleBackButton);
      return () => window.removeEventListener("popstate", handleBackButton);
    }
  }, [isStarted, submitted]);

  // Detect Tab Switch & Fullscreen Exit
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isStarted && !submitted) {
        alert("Warning: Tab switching is not allowed!");
      }
    };
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && isStarted && !submitted) {
        alert("Warning: You exited Fullscreen! Please stay in fullscreen to complete the practice set.");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [isStarted, submitted]);

  const typesetMath = (root) => {
    if (!root) return;
    if (!window.MathJax || !window.MathJax.typesetPromise) {
      setTimeout(() => typesetMath(root), 200);
      return;
    }
    if (window.MathJax.typesetClear) window.MathJax.typesetClear([root]);
    window.MathJax.typesetPromise([root]);
  };

  useEffect(() => {
    const loadQuestions = async () => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams();
        params.set("subjectId", subjectId);
        if (selectedSetId) params.set("questionSetId", selectedSetId);
        params.set("type", "PRACTICE");

        const res = await axios.get(`/master/mock-test/attempt-questions?${params.toString()}`);
        const rows = Array.isArray(res.data?.data) ? res.data.data : [];
        const resolvedSet = res.data?.set || null;

        setQuestions(rows);
        setActiveSet(resolvedSet);
        setAnswers({});
        setSavedMap({});
        setMarkedMap({});
        setCurrentIndex(0);
        setSubmitted(false);
        setIsStarted(false);
        setAttemptSaved(false);

        const resolvedSetId = String(resolvedSet?.questionSetId || "");
        if (resolvedSetId && resolvedSetId !== selectedSetId) {
          const nextParams = new URLSearchParams(searchParams);
          nextParams.set("questionSetId", resolvedSetId);
          setSearchParams(nextParams, { replace: true });
        }
      } catch (err) {
        setError(err?.response?.data?.message || "FAILED TO LOAD PRACTICE SET QUESTIONS");
      } finally {
        setLoading(false);
      }
    };

    if (subjectId) loadQuestions();
  }, [subjectId, selectedSetId, searchParams, setSearchParams]);

  useEffect(() => {
    if (submitted || loading || !isStarted) return undefined;
    if (timeLeft <= 0) {
      setSubmitted(true);
      if (document.fullscreenElement) document.exitFullscreen();
      return undefined;
    }
    const timer = setInterval(() => setTimeLeft((p) => p - 1), 1000);
    return () => clearInterval(timer);
  }, [submitted, loading, isStarted, timeLeft]);

  useEffect(() => {
    typesetMath(mathRef.current);
  }, [currentIndex, questions, submitted, loading, isStarted]);

  const score = useMemo(() => {
    let correct = 0; let attempted = 0; let totalMarks = 0; let obtained = 0;
    const perQuestion = {};
    questions.forEach((q, idx) => {
      const qKey = getQKey(q, idx);
      const marks = Number(q.marks || 1);
      const negative = Number(q.negativeMarks || 0);
      const answer = answers[qKey] || "";
      totalMarks += marks;
      if (answer) attempted += 1;
      if (answer === q.correctOption) {
        correct += 1; obtained += marks; perQuestion[qKey] = +marks;
      } else if (answer) {
        obtained -= negative; perQuestion[qKey] = -negative;
      } else {
        perQuestion[qKey] = 0;
      }
    });
    return { correct, attempted, total: questions.length, totalMarks, obtained, perQuestion };
  }, [answers, questions]);

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const handleStartExam = () => {
    if (!agreed) return;
    if (containerRef.current?.requestFullscreen) {
      containerRef.current.requestFullscreen().catch(() => {
        alert("Fullscreen was blocked! Please allow it to start the practice set.");
      });
    }
    setTimeLeft(manualMinutes * 60);
    setIsStarted(true);
  };

  const currentQuestion = questions[currentIndex];
  const currentQKey = currentQuestion ? getQKey(currentQuestion, currentIndex) : "";
  const selected = currentQKey ? answers[currentQKey] || "" : "";

  const renderImages = (images) => {
    const list = Array.isArray(images) ? images.filter(Boolean) : [];
    if (!list.length) return null;
    return (
      <div className="mt-2 flex flex-wrap gap-2">
        {list.map((img, idx) => (
          <div key={`${img}-${idx}`} className="h-20 w-20 overflow-hidden rounded-xl border border-slate-200 bg-white sm:h-24 sm:w-24">
            <img src={img} alt="question" className="h-full w-full object-cover" />
          </div>
        ))}
      </div>
    );
  };

  const handleSaveAndNext = () => {
    if (!currentQuestion) return;
    if (selected) {
      setSavedMap((prev) => ({ ...prev, [currentQKey]: true }));
    }
    if (currentIndex < questions.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handleSubmitWithWarning = () => {
    const ok = window.confirm("Are you sure you want to submit this practice set?");
    if (!ok) return;
    setSubmitted(true);
    if (document.fullscreenElement) document.exitFullscreen();
  };

  const handleMarkForReview = () => {
    if (!currentQuestion) return;
    setMarkedMap((prev) => ({ ...prev, [currentQKey]: !prev[currentQKey] }));
  };

  const handleClearResponse = () => {
    if (!currentQuestion) return;
    setAnswers((prev) => ({ ...prev, [currentQKey]: "" }));
    setSavedMap((prev) => ({ ...prev, [currentQKey]: false }));
    setMarkedMap((prev) => ({ ...prev, [currentQKey]: false }));
  };

  const handleReattempt = () => {
    setSubmitted(false); setIsStarted(false); setAgreed(false);
    setAnswers({}); setSavedMap({}); setMarkedMap({});
    setCurrentIndex(0); setTimeLeft(0); setAttemptSaved(false);
  };

  const renderRichText = (value, className = "") => (
    <div
      className={className}
      dangerouslySetInnerHTML={{ __html: toHtml(value || "---") }}
    />
  );

  useEffect(() => {
    const saveAttemptHistory = async () => {
      if (!submitted || !activeSet?.questionSetId || attemptSaved) return;

      try {
        await axios.post("/master/mock-test/submit-attempt", {
          questionSetId: activeSet.questionSetId,
          totalQuestions: score.total,
          attemptedQuestions: score.attempted,
          correctAnswers: score.correct,
          wrongAnswers: Math.max(score.attempted - score.correct, 0),
          totalMarks: score.totalMarks,
          obtainedMarks: score.obtained,
          durationMinutes: manualMinutes,
          timeTakenSeconds: Math.max((manualMinutes * 60) - timeLeft, 0),
          answers: questions.map((q, idx) => {
            const qKey = getQKey(q, idx);
            const answer = answers[qKey] || "";
            const marks = Number(q.marks || 1);
            const negative = Number(q.negativeMarks || 0);
            const marksAwarded = !answer ? 0 : answer === q.correctOption ? marks : -negative;
            return {
              questionBankId: q.questionBankId || q._id,
              questionText: q.questionText,
              selectedOption: answer,
              correctOption: q.correctOption,
              isCorrect: Boolean(answer) && answer === q.correctOption,
              marksAwarded,
            };
          }),
        });
        setAttemptSaved(true);
      } catch (err) {
        console.error("Failed to save practice attempt history", err);
      }
    };

    saveAttemptHistory();
  }, [submitted, activeSet, attemptSaved, score, manualMinutes, timeLeft, questions, answers]);

  if (loading) return <div className="p-6 text-sm font-semibold text-slate-500">Loading practice set...</div>;
  if (error) return <div className="p-6 text-sm font-semibold text-red-600">{error}</div>;
  if (!questions.length) return <div className="p-6 text-sm font-semibold text-slate-500">No questions found in this practice set.</div>;

  if (!isStarted && !submitted) {
    return (
      <div className={masterAttemptModalOverlay}>
        <div className={masterAttemptModalBox}>
          <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-4">
            <div>
              <h2 className="text-base font-bold text-slate-900 tracking-tight">Practice Set Instructions</h2>
              <p className="text-[11px] font-bold text-emerald-600 mt-0.5">{activeSet?.subjectName || questions[0]?.subjectName || "Practice Module"}</p>
            </div>
            <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-bold text-emerald-700 uppercase tracking-wider border border-emerald-100">Practice Mode</span>
          </div>

          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="rounded-2xl bg-slate-50 border border-slate-100 p-2.5 text-center">
              <p className="text-[10px] font-bold text-slate-400 uppercase">Questions</p>
              <p className="text-sm font-bold text-slate-800 mt-0.5">{questions.length}</p>
            </div>
            <div className="rounded-2xl bg-slate-50 border border-slate-100 p-2.5 text-center">
              <p className="text-[10px] font-bold text-slate-400 uppercase">Total Marks</p>
              <p className="text-sm font-bold text-slate-800 mt-0.5">{score.totalMarks}</p>
            </div>
            <div className="rounded-2xl bg-slate-50 border border-slate-100 p-2.5 text-center">
              <p className="text-[10px] font-bold text-slate-400 uppercase">Duration</p>
              <div className="flex items-center justify-center gap-1 mt-0.5">
                <input
                  type="number"
                  value={manualMinutes}
                  onChange={(e) => setManualMinutes(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-10 bg-transparent text-center text-sm font-bold text-emerald-600 outline-none"
                />
                <span className="text-[10px] font-semibold text-slate-500">m</span>
              </div>
            </div>
          </div>

          <div className="space-y-3 mb-5">
            <label className="flex cursor-pointer items-start gap-2.5 rounded-2xl border border-slate-100 p-3 bg-slate-50/50 hover:bg-slate-50 transition">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-[11px] leading-relaxed text-slate-600 font-medium">
                I agree to maintain full-screen mode and complete this practice set assessment.
              </span>
            </label>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => window.history.back()}
              className={masterAttemptBackBtn}
            >
              Back
            </button>
            <button
              onClick={handleStartExam}
              disabled={!agreed}
              className={agreed ? masterAttemptStartBtnActive : masterAttemptStartBtnDisabled}
            >
              Start Practice
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={`${masterAttemptContainer} ${
        isStarted && !submitted ? masterAttemptContainerActive : ""
      } ${submitted ? "overflow-y-auto" : "overflow-hidden"}`} 
    >
      <div className={masterAttemptHeaderCard} ref={mathRef}>
        <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
          <div className="space-y-3">
            <div>
              <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">{activeSet?.setName || "Practice Set"}</h1>
              <p className="mt-1 text-xs font-semibold text-slate-500 sm:text-sm">
                Topic: {activeSet?.topicName || activeSet?.subTopicName || questions[0]?.topicName || questions[0]?.subTopicName || questions[0]?.subjectName || "-"} | Total Q: {questions.length}
              </p>
            </div>
          </div>

          {!submitted ? (
            <div className={`${masterAttemptSummaryCard} border-emerald-200 bg-emerald-50 text-emerald-900`}>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600">Time Remaining</p>
              <p className={`text-lg font-semibold ${timeLeft < 60 ? "text-red-600 animate-pulse" : ""}`}>
                {formatTime(timeLeft)}
              </p>
            </div>
          ) : null}
        </div>
      </div>

      {!submitted ? (
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 xl:grid-cols-[minmax(0,1fr)_280px]">
          <div className={masterAttemptQuestionCard}>
            <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="mb-3 text-sm font-semibold text-slate-900 sm:text-base">
                <span className="mr-1">{`Q${currentIndex + 1}.`}</span>
                {renderRichText(currentQuestion.questionText, "inline")}
              </div>
              {renderImages(currentQuestion.questionImages)}

              <div className="space-y-2">
                {[
                  { key: "A", val: currentQuestion.optionA },
                  { key: "B", val: currentQuestion.optionB },
                  { key: "C", val: currentQuestion.optionC },
                  { key: "D", val: currentQuestion.optionD },
                ].map((opt) => (
                  <label
                    key={`${currentQKey}-${opt.key}`}
                    className={`${masterAttemptOptionCard} ${
                      selected === opt.key ? masterAttemptOptionSelected : masterAttemptOptionUnselected
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <input
                        type="radio"
                        name={currentQKey}
                        value={opt.key}
                        checked={selected === opt.key}
                        onChange={(e) => {
                          const val = e.target.value;
                          setAnswers((prev) => ({ ...prev, [currentQKey]: val }));
                          setSavedMap((prev) => ({ ...prev, [currentQKey]: Boolean(val) }));
                        }}
                        className="mt-1"
                      />
                      <div className="text-sm font-semibold text-slate-800">
                        <span className="mr-1">{`${opt.key}.`}</span>
                        {renderRichText(opt.val, "inline")}
                      </div>
                    </div>
                    {renderImages(currentQuestion.optionImages?.[opt.key])}
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 border-t border-slate-100 pt-3 sm:flex sm:flex-wrap">
              <button type="button" onClick={() => setCurrentIndex((prev) => Math.max(prev - 1, 0))} className={masterAttemptNavBtnPrev}>Previous</button>
              <button type="button" onClick={handleClearResponse} className={masterAttemptNavBtnClear}>Clear Response</button>
              <button type="button" onClick={handleMarkForReview} className={masterAttemptNavBtnReview}>Mark Review</button>
              <button type="button" onClick={handleSaveAndNext} className={masterAttemptNavBtnSave}>Save & Next</button>
            </div>
          </div>

          <div className={masterAttemptSidebarCard}>
            <p className="mb-1 text-sm font-semibold text-slate-900">Questions ({questions.length})</p>
            <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="grid grid-cols-6 gap-2 sm:grid-cols-7 xl:grid-cols-5">
                {questions.map((q, idx) => {
                  const qKey = getQKey(q, idx);
                  const isCurrent = idx === currentIndex && !submitted;
                  const isMarked = Boolean(markedMap[qKey]);
                  const isAnswered = Boolean(answers[qKey]);
                  const isSaved = Boolean(savedMap[qKey]);

                  let statusClass = "bg-white text-slate-700 border-slate-300 font-medium";
                  if (isMarked && isAnswered) statusClass = "bg-purple-600 text-white border-purple-700 font-bold";
                  else if (isMarked) statusClass = "bg-violet-500 text-white border-violet-600 font-bold";
                  else if (isAnswered || isSaved) statusClass = "bg-emerald-500 text-white border-emerald-600 font-bold";
                  else if (isCurrent) statusClass = "bg-amber-400 text-slate-900 border-amber-500 font-bold";

                  return (
                    <button
                      key={qKey}
                      type="button"
                      onClick={() => setCurrentIndex(idx)}
                      className={`h-8 w-8 rounded-md border text-sm transition-all cursor-pointer ${statusClass}`}
                    >
                      {idx + 1}
                    </button>
                  );
                })}
              </div>
            </div>
            <button type="button" onClick={handleSubmitWithWarning} className={masterAttemptSubmitBtn}>Submit Practice</button>
          </div>
        </div>
      ) : (
        <div>
          <div className="mb-3 flex flex-col gap-3 rounded-[28px] border border-emerald-200 bg-emerald-50 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-2">
              <div className={`${masterAttemptSummaryCard} border-emerald-300 bg-white text-emerald-900`}>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600">Score</p>
                <p className="text-lg font-semibold">{formatDecimal(score.obtained)} / {formatDecimal(score.totalMarks)}</p>
              </div>
              <div className={`${masterAttemptSummaryCard} border-violet-300 bg-white text-violet-900`}>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-violet-600">Time Taken</p>
                <p className="text-lg font-semibold">{formatTime((manualMinutes * 60) - timeLeft)}</p>
              </div>
            </div>
            <button type="button" onClick={handleReattempt} className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 cursor-pointer">Reattempt</button>
          </div>

          <div className="space-y-3">
            {questions.map((q, idx) => {
              const qKey = getQKey(q, idx);
              const ans = answers[qKey] || "";
              const ok = ans === q.correctOption;
              return (
                <div key={qKey} className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="text-sm font-semibold text-slate-900 sm:text-base">
                    <span className="mr-1">{`Q${idx + 1}.`}</span>
                    {renderRichText(q.questionText, "inline")}
                  </div>
                  {renderImages(q.questionImages)}
                  <div className={`mt-3 rounded-2xl border p-3 ${ok ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}>
                    <p className="text-sm font-semibold text-slate-900">Your Answer: {ans || "Not Answered"} | Correct: {q.correctOption}</p>
                    {["A", "B", "C", "D"].map((optionKey) => (
                      <div key={optionKey} className={`mt-2 rounded-xl border p-3 ${q.correctOption === optionKey ? "border-emerald-300 bg-white" : ans === optionKey ? "border-red-300 bg-white" : "border-slate-200 bg-white/80"}`}>
                        <div className="text-sm font-semibold text-slate-800">
                          <span className="mr-1">{`${optionKey}.`}</span>
                          {renderRichText(q[`option${optionKey}`], "inline")}
                        </div>
                        {renderImages(q.optionImages?.[optionKey])}
                      </div>
                    ))}
                    <div className="mt-3 rounded-xl border border-slate-200 bg-white p-3">
                      {renderRichText(q.explanationText || "No explanation available.", "mt-1 text-sm font-semibold text-slate-700")}
                      {renderImages(q.explanationImages)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PracticeSetAttempt;